<template>
    <div class="card">
        <div class="card-body card-body--main">
            <form name="mainForm" @submit.prevent="onSubmit">
              <slot name="title">
                <h4 class="card-title">
                  <span>{{ title }}</span>
                  <slot v-if="submitPosition === 'top'" name="actions">
                    <button :class="{'disabled': !readyForSubmit}"
                      type="submit"
                      class="btn btn-success ini-btn float-right text-uppercase"
                    >
                      <i v-if="loader || customSubmitInProgress" class="zmdi zmdi-refresh zmdi-hc-fw rotating"></i>
                      {{ $t(submitLabel) }}
                    </button>
                  </slot>
                </h4>
              </slot>
                <div class="row">
                    <div class="col-sm-12" v-for="field in mainFields">
                        <component :is="getComponent(field)" v-bind="field" v-model="field.value"
                                   :maxLength="field['max-length']" :numberLength="field['number-length']"
                                   :type="field['type']"
                                   @input="handleFormDraft"
                                   @changed="componentValueChanged($event, field)"
                                   @validationCompleted="onValidationCompleted($event, field)"
                                   :readonly="readonly"

                        ></component>
                    </div>
                </div>
                <slot v-if="submitPosition === 'bottom'" name="actions">
                  <button :class="{'disabled': !readyForSubmit}"
                    type="submit"
                    class="btn btn-success ini-btn float-right text-uppercase"
                  >
                    <i v-if="loader || customSubmitInProgress" class="zmdi zmdi-refresh zmdi-hc-fw rotating"></i>
                    {{ $t(submitLabel) }}
                  </button>
                </slot>
            </form>
        </div>
    </div>
</template>

<script>
    import BaseField from '@/components/form_elements/Base'
    import TextAreaField from '@/components/form_elements/TextArea'
    import SwitchField from '@/components/form_elements/Switch'
    import PicklistField from '@/components/form_elements/Picklist'
    import DetailField from '@/components/form_elements/Detail'
    import DropZoneField from "@/components/form_elements/DropZone"
    import DatePicker from "@/components/form_elements/DatePicker"
    import TimePicker from "@/components/form_elements/TimePicker"
    import {diff} from "deep-diff"

    export default {
        data() {
            return {
                componentInfo: {},
                initialFormSerialization: false,
                readyForSubmit: !this.submitPossibleOnlyOnChanges,
                hasValidationErrorOnAttachments: {},
                attachments: {},
                attachmentsDirty: {}
            }
        },

        props: {
            title: {default: 'Main Fields'},
            titleIcon: {default: ''},
            mainFields: {
                type: Array, default: function () {
                    return []
                }
            },
            submitPosition: {
              type: String,
              default: 'top',
              validator: (value) => ['top', 'bottom'].includes(value),
            },
            submitLabel: {
              type: String,
              default: 'LBL_SAVE'
            },
            customSubmit: false,
            customSubmitInProgress: false,
            submitPossibleOnlyOnChanges: {
              type: Boolean,
              default: true
            },
            readonly: {
              type: Boolean,
              default: false
            }
        },

        components: {
            'base-field': BaseField,
            'textarea-field': TextAreaField,
            'picklist-field': PicklistField,
            'switch-field': SwitchField,
            'detail-field': DetailField,
            'dropzone-field': DropZoneField,
            DatePicker,
            TimePicker

        },

        $_veeValidate: {validator: "new"},
        updated() {
            if (!this.initialFormSerialization) {
                this.updateInitialFormSerialization()
            }
        },

        methods: {
            isAnyAttachmentDirty() {
                for(let [, value] of Object.entries(this.attachmentsDirty)) {
                    if(value) {
                        return true
                    }
                }
                return false
            },

            componentValueChanged(value, field) {
                if (field.type === 'picture') {
                    // if at least one picture is selected, add it to the attachments with the key of the field
                    // otherwise, mark the value of this field as deleted with file id (value of the field)
                    if(value.length > 0) {
                        this.attachments[field.name].value = value[0]
                        delete this.attachments['deleted_file__' + field.name]
                    } else {
                        this.attachments['deleted_file__' + field.name] = { value: field.value }
                        this.attachments[field.name].value = ''
                    }
                }
            },

            onValidationCompleted(validationFailed, field) {
                if (field.type === 'picture') {
                    if (!this.attachments[field.name].value && this.attachments[field.name].mandatory) {
                        this.hasValidationErrorOnAttachments[field.name] = true
                    } else {
                        this.hasValidationErrorOnAttachments[field.name] = validationFailed
                    }

                    this.updateAttachmentsDirty(field);
                    this.handleFormDraft()
                }
            },

            updateAttachmentsDirty(field) {
                this.mainFields.forEach(item => {
                    if (item.type === 'picture' && item.name == field.name) {
                        if(item.value) {
                            this.attachmentsDirty[field.name] = true
                        } else {
                            this.attachmentsDirty[field.name] = this.attachments[field.name].value
                        }
                    }
                })
            },

            handleFormDraft() {
                let serializedFormData = $('form', this.$el).serializeFormData()
                let attachmentReady = true

                Object.keys(this.hasValidationErrorOnAttachments).forEach(key => {
                    if (this.hasValidationErrorOnAttachments[key])
                        attachmentReady = false
                })

                if (this.submitPossibleOnlyOnChanges) {
                  this.readyForSubmit = (diff(this.initialFormSerialization, serializedFormData) || this.isAnyAttachmentDirty()) && attachmentReady;
                }
            },

            getComponent(fieldInfo) {
                if (!fieldInfo.editable) {
                    return 'detail-field'
                }

                switch (fieldInfo.type) {
                    case 'picklist':
                    case 'multipicklist':
                        return 'picklist-field'
                    case 'boolean':
                        return 'switch-field'
                    case 'text':
                        return 'textarea-field'
                    case 'picture':
                    case 'attachment':
                        return 'dropzone-field'
                    case 'date':
                        return 'date-picker'
                    case 'time':
                        return 'time-picker'
                    default:
                        return 'base-field'
                }
            },

            async onSubmit() {
                if (!this.readyForSubmit) {
                    return;
                }

                await this.$validator.validateAll()

                if (!this.errors.any() && !this.loader) {
                    const formData = this.getFormData();
                    const attachments = this.getAttachments();

                    if (this.customSubmit) {
                      this.$emit('submit', { formData, attachments });
                      return;
                    }

                    this.loader = true
                    var response = await Api.update(formData, attachments)
                    this.loader = false
                    if (response.success) {
                        AppHelper.notify(this.$t('LBL_SAVE_DONE'), 'info')
                        this.$emit('submit', { success: true})

                        // update initialFormSerialization variable for updating Send button status
                        this.updateInitialFormSerialization()
                    } else {
                        AppHelper.notify('Error occurred', 'warning')
                        this.$emit('submit', { success: false})
                    }
                }
            },

            getAttachments() {
                const attachments ={}
                Object.keys(this.attachments).forEach(key => {
                    if(this.attachments[key].value) {
                        attachments[key]=this.attachments[key].value
                    }
                })
                return attachments
            },

            getFormData() {
                let formData = $('form', this.$el).serializeFormData()
                this.appendEmptyFields(formData);
                for(let [key, value] of Object.entries(this.getAttachments())) {
                    if(typeof value === 'object') {
                        formData[key] = 'changed'
                    } else if(key.startsWith('deleted_file__')) {
                        formData[/deleted_file__(\w+)/.exec(key)[1]] = 'changed'
                    }
                }
                return formData
            },

            appendEmptyFields(formData) {
              let multiPickListFields = this.getFieldsByType('multipicklist').map(field => field.name);
              let formFields = Object.keys(formData);
              let emptyFields = multiPickListFields.filter(field => !formFields.includes(field));
              emptyFields.forEach(field => {
                formData[field] = '';
              });
            },

            getFieldsByType(fieldType) {
              return this.mainFields.filter(field => field.type === fieldType);
            },

            updateInitialFormSerialization() {
                this.initialFormSerialization = $('form', this.$el).serializeFormData()
                this.mainFields.forEach(item => {
                    if (item.type === 'picture') {
                        if (item.mandatory && !item.displayValue.id) {
                            this.hasValidationErrorOnAttachments[item.name] = true
                        } else {
                            this.hasValidationErrorOnAttachments[item.name] = false
                        }
                        this.attachments[item.name] = {value: item.displayValue.id, mandatory: item.mandatory}
                    }
                })
                this.attachmentsDirty = {}
                this.handleFormDraft()
            }
        }
    }
</script>
<style lang="scss">@import "@/assets/scss/components/_mainform.scss";</style>